import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { defineStore } from 'pinia'
import { obtainTeamFormEvents, shouldScheduleRefresh } from './ScoreboardWidget.helpers'
import { extendDictionary, initLocalization } from '@/__core__/localization/localization'
import { allSettled } from '@/__core__/utils/fetch'

const DEFAULT_PREV_MATCHES = {
  homeWins: { value: '-', label: '' },
  awayWins: { value: '-', label: '' },
  draws: { value: '-', label: '' },
  label: ''
}

/** @typedef {{ eventId: string, competitorId: string, competitor2Id: string, sportType: string, languageIdentifier: string, region: string }} Payload */
export const useScoreboardStore = defineStore('scoreboard', {
  state: () => ({
    initial: {},
    scoreboard: {
      previousMatches: DEFAULT_PREV_MATCHES,
      form: {
        home: [],
        away: [],
        label: ''
      }
    },
    localization: {},
    drfRetryCounter: 0
  }),

  getters: {
    getForm() {
      return this.scoreboard.form || []
    },
    getVenueDetails() {
      return this.initial.venueDetails || false
    },
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    },
    /** @param {'home' | 'away'} teamQualifier */
    getTeamEvents() {
      return (teamQualifier, eventsNumber) => obtainTeamFormEvents(this.getForm[teamQualifier], eventsNumber)
    }
  },
  actions: {
    getShouldCallApi() {
      return shouldScheduleRefresh(this.initial)
    },
    /** @param {Payload} payload */
    async fetchScoreboardData(payload) {
      try {
        const [, responseLocalization, responseScoreboard, responseInitial] = await allSettled([
          initLocalization(payload.languageIdentifier),
          SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'scoreboard' }),
          SportsModeServiceRepository.Scoreboard.get(payload),
          SportsModeServiceRepository.Initial.get(payload)
        ])

        if (!responseLocalization) {
          throw new Error('Localization data not found')
        }

        extendDictionary(responseLocalization.data, payload.languageIdentifier)
        this.localization = responseLocalization.data

        if (!responseScoreboard) {
          throw new Error('Scoreboard data not found')
        }

        this.scoreboard = responseScoreboard.data

        if (!this.scoreboard.previousMatches) {
          this.scoreboard.previousMatches = DEFAULT_PREV_MATCHES
        }

        if (!responseInitial) {
          throw new Error('Initial data not found')
        }

        const initial = responseInitial.data
        this.initial = initial
      } catch (error) {
        throw new Error(`[Scoreboard.store, fetchScoreboardData]: ${error.message}`)
      }
    },
    /** @param {Payload} payload */
    async scheduleScoreboardRefresh(payload) {
      try {
        const responseInitial = await SportsModeServiceRepository.Initial.get(payload)
        this.initial = responseInitial.data
      } catch {
        this.drfRetryCounter++
      }
    }
  }
})
